import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Alert from '@mui/material/Alert';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ColorlibConnector, PortfolioOrderStepIcon } from '../../components/Stepper';
import { useAppDispatch } from '../../redux/hooks';
import { fetchOrderExecutionByOrderId } from '../../redux/modules/orderExecution/orderExecution.actions';
import { orderExecutionByOrderIdSelector } from '../../redux/modules/orderExecution/orderExecution.selectors';
import {
  cleanViewDetailsByOrderId,
  fetchConsolidateRequestsByOrderId,
  fetchFundOrdersByOrderId,
  fetchPortfolioOrdersByOrderId,
  fetchSatelliteRequestsByOrderId,
  fetchUserPortfolioRequestsByOrderId,
} from '../../redux/modules/portfolioOrder/portfolioOrder.actions';
import { viewDetailsByOrderIdSelector } from '../../redux/modules/portfolioOrder/portfolioOrder.selectors';
import { ReducerState } from '../../redux/store.types';
import { locations } from '../../routes/locations';
import {
  FundOrder,
  FundOrderType,
  PortfolioOrderExecutionStatus,
  PortfolioOrderStatus,
} from '../../types/portfolioOrder';
import { PortfolioOrderActions } from './PortfolioOrderActions';
import { PortfolioOrderTabList } from './PortfolioOrderTabList';
import { fetchUserFeeDeductions } from '../../redux/modules/userFeeDeduction/userFeeDeduction.actions';
import { UserFeeDeductionStatus } from '../../types/userFeeDeduction';
import { fetchOrdersByMadkholOrderId } from '../../redux/modules/jadwa/jadwa.actions';
import { jadwaSelector } from '../../redux/modules/jadwa/jadwa.selectors';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { getJadwaExecutionFileBlockReason } from './components/jadwa/helpers';
import { JadwaOrderSide } from '../../types/jadwa';

const steps = ['File execution', 'Review and Validation', 'Reconciliation', 'Approved'];

const mapPortfolioOrderStatusToStep: Record<string, number> = {
  [PortfolioOrderStatus.IN_PROGRESS]: 0,
  [PortfolioOrderStatus.EXECUTED]: 0,
};

const mapOrderExecutionStatusToStep: Record<string, number> = {
  [PortfolioOrderExecutionStatus.EXECUTION]: 0,
  [PortfolioOrderExecutionStatus.IN_REVIEW]: 1,
  [PortfolioOrderExecutionStatus.RECONCILIATION]: 2,
  [PortfolioOrderExecutionStatus.APPROVED]: 3,
};

export function PortfolioOrder() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [fundOrdersKey, setFundOrdersKey] = useState<string>(`${Date.now()}`);
  const [executionTemplateData, setExecutionTemplateData] = useState<IAnyObject[]>([]);
  const [selectedFundOrders, setSelectedFundOrders] = useState<FundOrder[]>([]);
  const orderExecution = useSelector((state: ReducerState) => orderExecutionByOrderIdSelector(state, id || ''));
  const { portfolioOrders, fundOrders, satelliteAccountRequests } = useSelector(viewDetailsByOrderIdSelector);
  const portfolioOrderStatus = portfolioOrders.length && portfolioOrders?.[0].status?.name;
  const showAlpacaTab = fundOrders.some((fo) => fo.indexFund.source?.name?.toLowerCase()?.includes('alpaca'));
  const showJadwaTab = fundOrders.some((fo) => fo.indexFund.manager?.nameEn?.toLowerCase()?.includes('jadwa'));
  const showAlbiladTab = fundOrders.some((fo) => fo.indexFund.manager?.nameEn?.toLowerCase()?.includes('albilad'));
  const showDerayahTab = fundOrders.some((fo) => fo.indexFund.manager?.nameEn?.toLowerCase()?.includes('derayah'));
  // Same name match madkhol-jadwa pulls SNB fund orders by (`fund_manager.name_en LIKE '%snb%'`).
  const showSnbTab = fundOrders.some((fo) => fo.indexFund.manager?.nameEn?.toLowerCase()?.includes('snb'));
  const [warning, setWarning] = useState<string>('');
  // Jadwa / Albilad / SNB (all live in madkhol-jadwa): the execution file stays closed until every transfer is approved.
  const hasManualManagerFunds = showJadwaTab || showAlbiladTab || showSnbTab;
  // Jadwa / Albilad / SNB SUBSCRIPTIONS only. A redemption's FX is booked at import on the sold amount, so it has no
  // transfer before import — counting it (or another manager's subscription) would block the file for good.
  const isManualManagerFund = (fo: FundOrder) =>
    /jadwa|albilad|snb/.test(fo.indexFund.manager?.nameEn?.toLowerCase() ?? '');
  const manualSubscriptionFundOrderIds = fundOrders
    .filter((fo) => fo.type?.name === FundOrderType.SUBSCRIPTION && isManualManagerFund(fo))
    .map((fo) => fo.id);
  const hasSubscriptionFunds = manualSubscriptionFundOrderIds.length > 0;
  const { orders: jadwaOrders, fetchOrdersStatus: jadwaOrdersStatus } = useSelector(jadwaSelector);
  const managerOrders = jadwaOrders.filter((order) => order.orderId === id && order.side === JadwaOrderSide.BUY);
  const isManagerOrdersLoaded = jadwaOrdersStatus === fetchStatuses.success;
  let executionFileBlockReason: string | null = null;

  if (hasManualManagerFunds && hasSubscriptionFunds) {
    if (isManagerOrdersLoaded) {
      executionFileBlockReason = getJadwaExecutionFileBlockReason(managerOrders, manualSubscriptionFundOrderIds);
    } else if (jadwaOrdersStatus === fetchStatuses.rejected) {
      executionFileBlockReason =
        'Could not load the Jadwa / Albilad / SNB transfers, so export and import stay blocked. Reload to try again.';
    } else {
      executionFileBlockReason = 'Checking the Jadwa / Albilad / SNB transfers…';
    }
  }

  useEffect(() => {
    if (id && hasManualManagerFunds) {
      dispatch(fetchOrdersByMadkholOrderId(id));
    }
  }, [id, hasManualManagerFunds]);

  const fetchPortfolioOrders = useCallback(() => {
    if (id) {
      dispatch(fetchPortfolioOrdersByOrderId({ limit: 10, offset: 0, orderId: id }));
    }
  }, []);

  const fetchOrderExecution = useCallback(() => {
    if (id) {
      dispatch(fetchOrderExecutionByOrderId(id));
    }
  }, []);

  const fetchConsolidateRequests = useCallback(() => {
    if (id) {
      dispatch(fetchConsolidateRequestsByOrderId({ limit: 10, offset: 0, orderId: id }));
    }
  }, []);

  const fetchUserPortfolioRequests = useCallback(() => {
    if (id) {
      dispatch(fetchUserPortfolioRequestsByOrderId({ limit: 10, offset: 0, orderId: id }));
    }
  }, []);

  const fetchSatelliteRequests = useCallback(() => {
    if (id) {
      dispatch(fetchSatelliteRequestsByOrderId({ limit: 10, offset: 0, orderId: id }));
    }
  }, []);

  useEffect(() => {
    if (!id) {
      return navigate(locations.portfolioOrderList(), { replace: true });
    }

    dispatch(fetchFundOrdersByOrderId({ orderId: id, limit: 25 }));
    dispatch(fetchSatelliteRequestsByOrderId({ orderId: id }));
  }, [id]);

  const checkPendingFees = useCallback(async () => {
    if (id) {
      const userPortfolioRequests = await dispatch(
        fetchUserPortfolioRequestsByOrderId({ limit: 10, offset: 0, orderId: id }),
      ).unwrap();
      const userIds = userPortfolioRequests.data.items
        .filter((upr) => upr.changes.redeemAll === true)
        .map((upr) => upr.userPortfolio?.userId);

      if (userIds.length > 0) {
        const deductions = await dispatch(
          fetchUserFeeDeductions({
            filter: JSON.stringify({
              items: [
                {
                  columnField: 'userId',
                  operatorValue: 'isAnyOf',
                  value: userIds,
                },
                {
                  columnField: 'status.name',
                  operatorValue: 'not',
                  value: UserFeeDeductionStatus.APPROVED,
                },
              ],
            }),
            requestId: '',
          }),
        ).unwrap();
        if (deductions.data.totalItems > 0) {
          setWarning('THIS_ORDER_HAS_UNPROCESSED_FEES');
        }
      }
    }
  }, []);

  useEffect(() => {
    fetchPortfolioOrders();
    fetchOrderExecution();
    fetchSatelliteRequests();
    fetchConsolidateRequests();
    fetchUserPortfolioRequests();
    checkPendingFees();

    return () => {
      dispatch(cleanViewDetailsByOrderId);
    };
  }, []);

  useEffect(() => {
    const data = fundOrders.map((fo) => {
      const accountRequest = satelliteAccountRequests.find((sar) => sar?.fundOrder?.id === fo.id) || {
        amount: 0,
        numOfUnits: 0,
      };

      return {
        fundOrderId: fo.id,
        numOfUnits: fo.sharesCount + accountRequest.numOfUnits,
        actualAmount: fo.estimatedAmount + accountRequest.amount,
        status: 'done',
      };
    });

    setExecutionTemplateData(data);
  }, [fundOrders, satelliteAccountRequests]);

  const onSelectFundOrders = (ids: FundOrder[]) => setSelectedFundOrders(ids);

  const getPortfolioOrderStep = () => {
    if (orderExecution) {
      return mapOrderExecutionStatusToStep[orderExecution?.status?.name];
    }

    return mapPortfolioOrderStatusToStep[portfolioOrderStatus];
  };

  return (
    <Container component="main" maxWidth="xl">
      {getPortfolioOrderStep() !== undefined && (
        <Stack sx={{ width: '100%', paddingBottom: 2 }} spacing={4}>
          <Stepper alternativeLabel activeStep={getPortfolioOrderStep()} connector={<ColorlibConnector />}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel StepIconComponent={PortfolioOrderStepIcon}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Stack>
      )}
      {warning && <Alert severity="warning">{warning}</Alert>}
      {executionFileBlockReason && (
        <Alert severity="warning" sx={{ mt: warning ? 1 : 0 }}>
          {executionFileBlockReason}
        </Alert>
      )}

      {id && (
        <PortfolioOrderActions
          orderId={id}
          setFundOrdersKey={setFundOrdersKey}
          selectedFundOrders={selectedFundOrders}
          executionTemplateData={executionTemplateData}
          fetchPortfolioOrders={fetchPortfolioOrders}
          fetchOrderExecution={fetchOrderExecution}
          fetchSatelliteRequests={fetchSatelliteRequests}
          fetchConsolidateRequests={fetchConsolidateRequests}
          executionFileBlockReason={executionFileBlockReason}
        />
      )}

      <PortfolioOrderTabList
        orderId={id}
        fundOrdersKey={fundOrdersKey}
        onSelectFundOrders={onSelectFundOrders}
        showAlpacaTab={showAlpacaTab}
        showJadwaTab={showJadwaTab}
        showAlbiladTab={showAlbiladTab}
        showDerayahTab={showDerayahTab}
        showSnbTab={showSnbTab}
      />
    </Container>
  );
}
